import { Drawer } from "components";
import { useGetAllTickets, useGlobalStore, useMainStore } from "controller";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type GetAllTicketQueryProps,
  statusData,
  type TicketIntervalType,
} from "shared";
import { TicketsTable } from "shared/features/tickets";
import { defaultTablePerPageCount } from "utilz/helpers/preference-helpers/defaultTablePreferenceData";

type TicketWidget = NonNullable<GetAllTicketQueryProps["widget"]>;

interface TicketFilterProps {
  type?: string;
  /**
   * Resources the table is scoped to. Empty means unscoped — the drilldown for a
   * whole-team selection, where enumerating every id would be pointless.
   */
  resourceIds: Array<string>;
  view?: string;
  otherfilters?: Array<Record<string, unknown>>;
  interval?: TicketIntervalType;
}

type TicketRowData = { mapping_id?: string };

/**
 * Drawer `type` → the `widget` param that names the card to the API. The filters
 * alone can't: Active and Unassigned now send the same status predicate, so the
 * backend needs the card stated outright to apply that card's own set.
 *
 * Explicit on purpose — the legacy home's "assigned" type is a different card and
 * must stay widget-less. Closed isn't here yet: the backend windows it on
 * end_date and hasn't shipped that predicate.
 */
const TYPE_TO_WIDGET: Record<string, TicketWidget> = {
  overDue: "overDue",
  dueToday: "dueToday",
  unresolved: "assigned",
  unassigned: "unassigned",
};

export const TicketTableDrawer = ({
  onClose,
  open,
  filter,
}: {
  open: boolean;
  onClose: () => void;
  filter?: TicketFilterProps;
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const perPageCount = useMainStore((state) => state.perPageCount);
  const setPerPageCount = useMainStore((state) => state.setPerPageCount);
  const widget = TYPE_TO_WIDGET[filter?.type ?? ""];
  const timeZone = useGlobalStore((state) => state.timezone);
  const getHomeScreenFilter = () => {
    if (!filter) {
      return [];
    }
    switch (filter?.type) {
      case "unassigned":
        // The card counts every ticket nobody has picked up, not just the ones
        // still in NEW — so the drilldown drops the closed-out statuses instead
        // of pinning a single one, and asks the API for the unassigned bucket
        // explicitly rather than leaning on NEW as a proxy.
        return [
          {
            column: "resource_scheduling.resource_id",
            operator: "=",
            value: "unassigned",
          },
          {
            column: "tickets.ticket_status",
            operator: "<>",
            value: [
              statusData.CLOSED,
              statusData.CANCELLED,
              statusData.COMPLETED,
              statusData.MERGED,
            ],
          },
        ];
      case "unresolved":
        return [
          {
            column: "tickets.ticket_status",
            operator: "<>",
            value: [statusData.CLOSED, statusData.CANCELLED, statusData.MERGED],
          },
        ];
      case "atRisk":
        return [
          {
            column: "sla_status",
            operator: "=",
            value: "at_risk",
          },
        ];
      default:
        return [];
    }
  };

  const { data: allTickets, isLoading: isAllTicketsLoading } = useGetAllTickets(
    {
      filter: [
        ...(!["unassigned", "newArrivals", "closed"].includes(
          filter?.type || "",
        ) && filter?.resourceIds?.length
          ? [
              {
                column: "resource_scheduling.resource_id",
                operator: "=",
                value: filter.resourceIds,
              },
            ]
          : []),
        ...getHomeScreenFilter(),
        ...(filter?.otherfilters || []),
      ],
      pageNumber: page,
      perPageCount: perPageCount?.ticket || defaultTablePerPageCount,
      sortColumn: "created_at",
      sortDirection: "desc",
      interval: filter?.interval,
      timeZone: timeZone,
      ...(widget && { widget }),
    },
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      drawerStyle={{ width: "100vw", maxWidth: "1080px" }}
      title="Tickets"
      footer={<></>}
    >
      <TicketsTable
        currentPageNumber={page}
        setCurrentPageNumber={setPage}
        setItemsPerPage={(itemsPerPage) => {
          setPerPageCount({ type: "ticket", count: itemsPerPage });
        }}
        itemsPerPage={perPageCount?.ticket || defaultTablePerPageCount}
        data={!isAllTicketsLoading && allTickets}
        handleRowClick={(rowData: TicketRowData) =>
          navigate(`/app/service-desk/tickets/${rowData?.mapping_id}`)
        }
        isDataLoading={isAllTicketsLoading}
      />
    </Drawer>
  );
};
